import _ from 'lodash';
import { ApolloError, FetchResult, MutationResult } from '@apollo/client';
import React, { useState } from 'react';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';

// Data
import { ICreatePlayerContainer, ICreatePlayerFields } from './create-player.interface';
import { GENDER_OPTIONS } from '../../../lib/constants';
import { staffUser } from '../../../lib/services/auth.service';
import { formatDateOnly } from '../../../lib/helpers/date.helpers';
import { readPlayerNameConflict } from '../../../lib/helpers/player.helpers';
import { dateOfBirthSchema } from '../../../lib/helpers/player.validation';
import { readUserInputMessage } from '../../../lib/helpers/graphql-error.helpers';
import { getCreatePlayerMessages } from './create-player.messages';

// Components
import { CreatePlayer } from './create-player.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { SportTerminology } from '../../../lib/types';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';

export const CreatePlayerContainer: React.FC<ICreatePlayerContainer> = ({
  createPlayerMutation,
  ageGroups,
  fixedAgeGroup,
  createAgeGroup,
  positions,
  fetchError,
  loading
}) => {
  const termLookup = useCompanySportTerminology();
  const athleteTerm = termLookup[SportTerminology.ATHLETE];
  const messages = getCreatePlayerMessages(termLookup);

  const [submitError, setSubmitError] = useState<React.ReactNode>(null);
  const [showSaved, setShowSaved] = useState(false);

  const INITIAL_FORM_VALUES = {
    firstName: '',
    lastName: '',
    nickname: '',
    date: dayjs().subtract(8, 'year').format('YYYY/MM/DD'),
    gender: GENDER_OPTIONS[1],
    externalId: '',
    // Seeded so a failed submit can mark them touched: Formik walks values, not
    // the schema, so a field absent here never renders its error.
    positionId: null,
    ageGroupId: fixedAgeGroup ? { value: fixedAgeGroup._id, label: fixedAgeGroup.name } : null,
    contacts: [{ name: '', email: '', sendEmail: true, phone: '', sendSMS: false, height: '', relationship: null, sportInvolvement: null }]
  };

  async function handleSubmit(values: ICreatePlayerFields, { setSubmitting, resetForm, setFieldValue }: FormikHelpers<ICreatePlayerFields>) {
    setSubmitError(null);

    function complete(result: MutationResult) {
      setSubmitting(false);

      if (result.error) {
        console.error('createPlayerAssessment failed:', result.error);
        setSubmitError(<IntlMsg {...messages.errSubmitFailed} />);
      } else {
        setShowSaved(true);
        setTimeout(() => {
          setShowSaved(false);
        }, 2000);

        resetForm({
          values: {
            ...INITIAL_FORM_VALUES,
            positionId: values.positionId,
            ageGroupId: values.ageGroupId
          }
        });
        setFieldValue('primarySport', null)
      }
    }

    createPlayerMutation({
      player: {
        ..._.omit(values, ['positionId', 'ageGroupId', 'gender', 'contacts', 'date', 'primarySport']),
        dateOfBirth: values.date,
        gender: values.gender.value,
        primarySport: values.primarySport?.value,
        // Ships the trailing pending row too — see "The duplicate-contact rule
        // lives on the server" in CLAUDE.md.
        contacts: values.contacts?.map((c) => ({
          ..._.omit(c, ['height', 'relationship', 'sportInvolvement']),
          height: c.height ? Number(c.height) : undefined,
          relationship: c.relationship?.value,
          sportInvolvement: c.sportInvolvement?.value
        }))
      },
      assessmentId: fixedAgeGroup?.assessmentId || staffUser()?.settings.activeAssessmentId,
      ageGroupId: values.ageGroupId?.value,
      positionId: values.positionId?.value
    })
      .then(complete)
      .catch((error: ApolloError) => {
        console.error('createPlayerAssessment failed:', error);
        setSubmitting(false);

        // A name collision refuses the create without saying why a nickname would
        // help, so the refusal describes the record the name collided with.
        const conflict = readPlayerNameConflict(error);

        // The generic message is the last resort, never the first answer — a refusal
        // the server wrote for the user is shown instead. See REACT_CONVENTIONS.md.
        if (!conflict) return setSubmitError(readUserInputMessage(error) || <IntlMsg {...messages.errSubmitFailed} />);

        const name = `${values.firstName} ${values.lastName}`.trim();

        // A colliding record with no stored date leaves nothing to enter that
        // would match it, so that refusal offers only the nickname way out.
        if (!conflict.dateOfBirth) return setSubmitError(<IntlMsg {...messages.errNameTakenUnknownDob} values={{ name }} />);

        return setSubmitError(<IntlMsg {...messages.errNameTaken} values={{ name, dateOfBirth: formatDateOnly(conflict.dateOfBirth) }} />);
      })
  }

  const ageGroupOptions = ageGroups.map(ageGroup => ({ value: ageGroup._id, label: ageGroup.name }));
  const handleCreateAgeGroup = createAgeGroup && ((form: FormikProps<ICreatePlayerFields>, name: string) => {
    createAgeGroup(name).then((result: FetchResult) => {
      if (result.data && result.data.createAgeGroup) {
        form.setFieldValue('ageGroupId', ({ value: result.data.createAgeGroup._id, label: name }));
        form.setFieldTouched('ageGroupId');
      }
    });
  });

  const positionOptions = positions.map(position => ({ value: position._id, label: position.name }));

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required(`Enter the ${athleteTerm}'s first name`),
    lastName: Yup.string().required(`Enter the ${athleteTerm}'s last name`),
    date: dateOfBirthSchema(`Enter the ${athleteTerm}'s date of birth`),
    // Both are ID! on the wire, so an unselected one is refused by the server
    // with nothing the operator can act on. They are options, hence object().
    positionId: Yup.object().nullable().required('Select a position'),
    ageGroupId: Yup.object().nullable().required('Select an age group'),
  });

  return (
    <Formik
      initialValues={INITIAL_FORM_VALUES}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {form => <CreatePlayer
        form={form}
        submitError={submitError}
        showSaved={showSaved}
        ageGroupOptions={ageGroupOptions}
        ageGroupFixed={Boolean(fixedAgeGroup)}
        createAgeGroup={handleCreateAgeGroup}
        positionOptions={positionOptions}
        fetchError={fetchError}
        loading={loading}
      />}
    </Formik>
  );
};
